import { useEffect } from 'react';
import { useTracker } from '@archipro-website/tracker';
import type { TrackingEvent } from '@archipro-website/tracker';

/** Directory browse surfaces that report a list impression on view. */
export type DirectoryListImpressionEvent = Extract<
    TrackingEvent,
    | 'productListImpression'
    | 'projectListImpression'
    | 'professionalListImpression'
    | 'articleListImpression'
>;

interface DirectoryListImpressionArgs {
    event: DirectoryListImpressionEvent;
    /**
     * Series key. The legacy pages distinguished their surfaces with `label`
     * ('Dentsu' vs 'Rooms'), which never survived the pipeline — so rooms and
     * projects were indistinguishable once they shared an event name. `Source`
     * is one of the two keys that does survive, so the surface goes here.
     */
    source: string;
    /** Selected refinements. Travel in `ExtraData`; see below. */
    dimensions: Record<string, string | undefined>;
}

/**
 * Reports "a reader viewed this directory, with these refinements".
 *
 * The legacy directories each fired their own copy of this effect from inside
 * the legacy page component. The revamp routes render a different component, so
 * the effect never mounted and all three series went to zero at flag-on — one
 * shared hook so the four surfaces cannot drift apart again.
 *
 * Payload shape follows `use-prompt-impression`: the flat keys stay for
 * network-level QA, and the same values ride in `ExtraData` as JSON because
 * only `Source` and `ExtraData` survive the warehouse's column flattening. The
 * legacy payload put `category` / `brand` / `region` at the top level, where
 * they were dropped — restoring the row without this would restore a series
 * with no dimensions on it.
 */
export const useDirectoryListImpression = ({
    event,
    source,
    dimensions,
}: DirectoryListImpressionArgs): void => {
    const tracker = useTracker();
    // Serialised rather than the object, so a fresh object literal per render
    // does not re-fire the impression. It is also exactly what `ExtraData`
    // carries, so there is nothing to keep in sync.
    const serialised = JSON.stringify(dimensions);

    useEffect(() => {
        void tracker.log(event, {
            url: new URL(window.location.href),
            data: {
                ...(JSON.parse(serialised) as Record<
                    string,
                    string | undefined
                >),
                Source: source,
                ExtraData: serialised,
            },
        });
    }, [event, source, serialised, tracker]);
};
